import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { PIN_ICONS } from './CampaignMap';
import { colorFromUid } from '../utils/playerColors';

const getIconDef = (key) => PIN_ICONS.find(p => p.key === key)?.icon || faMapMarkerAlt;

const EMPTY_FORM = { label: '', description: '', icon: 'mapMarkerAlt' };
const DRAG_THRESHOLD = 4; // px avant de considérer un déplacement (vs un clic)
const DRAW_MIN_DIST = 0.4; // distance mini (%) entre deux points d'un trait
const STROKE_WIDTH = 3; // épaisseur écran (px, non déformée par le viewBox)

// Couche collaborative des joueurs (pins créables / déplaçables / éditables,
// dessin libre + undo), superposée à la carte du MJ.
// Ne touche jamais à la couche `pins/` du MJ.
const MapCollab = forwardRef(({ mapPath, user, authorName, toolMode, onConsumeTool, isDm = false }, ref) => {
  const [pins, setPins] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [selected, setSelected] = useState(null); // id du pin dont la tooltip est ouverte
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, above: false });
  const [form, setForm] = useState(null); // { id?, x, y, label, description, icon }
  const [dragPos, setDragPos] = useState(null); // aperçu live { id, x, y }
  const [drawPreview, setDrawPreview] = useState(null); // aperçu live du trait en cours
  const [confirm, setConfirm] = useState(null); // null | 'mine' | 'all' — modale de confirmation
  const layerRef = useRef(null);
  const dragState = useRef(null);
  const drawState = useRef(null);

  const myUid = user?.uid;
  // Couleur choisie dans les paramètres du compte, sinon repli sur la palette
  const myColor = user?.playerColor || colorFromUid(myUid);

  useEffect(() => {
    if (!mapPath) return;
    const pinsRef = firebase.database().ref(`${mapPath}/playerPins`);
    pinsRef.on('value', snap => {
      const val = snap.val();
      setPins(val ? Object.entries(val).map(([id, p]) => ({ id, ...p })) : []);
    });
    const drawRef = firebase.database().ref(`${mapPath}/drawings`);
    drawRef.on('value', snap => {
      const val = snap.val();
      setDrawings(val ? Object.entries(val).map(([id, d]) => ({ id, ...d })) : []);
    });
    return () => { pinsRef.off(); drawRef.off(); };
  }, [mapPath]);

  // Undo : supprime mon trait le plus récent (exposé au parent via ref)
  useImperativeHandle(ref, () => ({
    undo: () => {
      const mine = drawings.filter(d => d.authorUid === myUid);
      if (!mine.length) return;
      const last = mine.reduce((a, b) => ((a.createdAt || 0) >= (b.createdAt || 0) ? a : b));
      firebase.database().ref(`${mapPath}/drawings/${last.id}`).remove();
    },
    clearMine: () => {
      if (!drawings.some(d => d.authorUid === myUid)) return;
      setConfirm('mine');
    },
    // MJ : efface toute la couche joueurs (pins + tracés de tout le monde)
    clearAll: () => {
      if (!pins.length && !drawings.length) return;
      setConfirm('all');
    },
    canUndo: drawings.some(d => d.authorUid === myUid),
  }), [drawings, pins, myUid, mapPath]);

  // Supprimer un tracé au clic (mode gomme) : son auteur, ou le MJ
  const deleteDrawing = (d) => {
    if (d.authorUid !== myUid && !isDm) return;
    firebase.database().ref(`${mapPath}/drawings/${d.id}`).remove();
  };

  const doConfirm = () => {
    if (confirm === 'mine') {
      const mine = drawings.filter(d => d.authorUid === myUid);
      const updates = {};
      mine.forEach(d => { updates[d.id] = null; });
      firebase.database().ref(`${mapPath}/drawings`).update(updates);
    } else if (confirm === 'all') {
      firebase.database().ref(`${mapPath}/playerPins`).remove();
      firebase.database().ref(`${mapPath}/drawings`).remove();
    }
    setConfirm(null);
  };

  const toPercent = (clientX, clientY) => {
    const rect = layerRef.current.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    return { x, y };
  };

  // Drag / clic sur un pin — géré par des listeners globaux montés une fois
  useEffect(() => {
    const onMove = (e) => {
      const st = dragState.current;
      if (!st) return;
      const dx = Math.abs(e.clientX - st.startX);
      const dy = Math.abs(e.clientY - st.startY);
      if (!st.moved && dx + dy < DRAG_THRESHOLD) return;
      if (!st.canDrag) return;
      st.moved = true;
      const { x, y } = toPercent(e.clientX, e.clientY);
      st.x = x;
      st.y = y;
      setDragPos({ id: st.id, x, y });
    };
    const onUp = () => {
      const st = dragState.current;
      dragState.current = null;
      setDragPos(null);
      if (!st) return;
      if (st.moved) {
        firebase.database()
          .ref(`${mapPath}/playerPins/${st.id}`)
          .update({ x: st.x, y: st.y });
      } else {
        // Simple clic → ouvrir la tooltip
        const rect = st.el.getBoundingClientRect();
        const above = rect.top > 220;
        setTooltipPos({ x: rect.left + rect.width / 2, y: above ? rect.top : rect.bottom, above });
        setSelected(prev => (prev === st.id ? null : st.id));
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [mapPath]);

  // Dessin libre — listeners globaux montés une fois
  useEffect(() => {
    const onMove = (e) => {
      const d = drawState.current;
      if (!d) return;
      const { x, y } = toPercent(e.clientX, e.clientY);
      const last = d.points[d.points.length - 1];
      if (last && Math.hypot(x - last.x, y - last.y) < DRAW_MIN_DIST) return;
      d.points.push({ x, y });
      setDrawPreview([...d.points]);
    };
    const onUp = () => {
      const d = drawState.current;
      drawState.current = null;
      setDrawPreview(null);
      if (!d) return;
      if (d.points.length >= 2) {
        const str = d.points.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
        firebase.database().ref(`${mapPath}/drawings`).push({
          authorUid: myUid,
          authorName: authorName || user.displayName || 'Joueur',
          color: myColor,
          width: STROKE_WIDTH,
          points: str,
          createdAt: firebase.database.ServerValue.TIMESTAMP,
        });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [mapPath, myUid, myColor, authorName, user]);

  const handleLayerPointerDown = (e) => {
    if (toolMode !== 'draw') return;
    if (e.target.closest('.playerPin')) return; // ne pas démarrer un trait sur un pin
    const { x, y } = toPercent(e.clientX, e.clientY);
    drawState.current = { points: [{ x, y }] };
    setDrawPreview([{ x, y }]);
  };

  const onPinPointerDown = (e, pin) => {
    e.stopPropagation();
    dragState.current = {
      id: pin.id,
      el: e.currentTarget,
      startX: e.clientX,
      startY: e.clientY,
      x: pin.x,
      y: pin.y,
      moved: false,
      canDrag: pin.authorUid === myUid, // seul l'auteur déplace son pin
    };
  };

  const handleLayerClick = (e) => {
    if (toolMode !== 'addPin') return;
    if (e.target !== layerRef.current) return; // clic sur le vide uniquement
    const { x, y } = toPercent(e.clientX, e.clientY);
    setSelected(null);
    setForm({ x, y, ...EMPTY_FORM });
    if (onConsumeTool) onConsumeTool();
  };

  const savePin = async () => {
    if (!form.label.trim()) {
      toast.error(i18next.t('map.labelRequired'));
      return;
    }
    const ref = firebase.database().ref(`${mapPath}/playerPins`);
    if (form.id) {
      await ref.child(form.id).update({
        label: form.label,
        description: form.description,
        icon: form.icon,
      });
    } else {
      await ref.push({
        x: form.x,
        y: form.y,
        label: form.label,
        description: form.description,
        icon: form.icon,
        authorUid: myUid,
        authorName: authorName || user.displayName || 'Joueur',
        color: myColor,
        createdAt: firebase.database.ServerValue.TIMESTAMP,
      });
    }
    setForm(null);
  };

  const deletePin = async (id) => {
    await firebase.database().ref(`${mapPath}/playerPins/${id}`).remove();
    setForm(null);
    setSelected(null);
  };

  const openEdit = (pin) => {
    setSelected(null);
    setForm({ id: pin.id, x: pin.x, y: pin.y, label: pin.label, description: pin.description || '', icon: pin.icon || 'mapMarkerAlt' });
  };

  const selectedPin = pins.find(p => p.id === selected);

  return (
    <>
      <div
        ref={layerRef}
        className={`mapCollabLayer${toolMode === 'addPin' ? ' adding' : ''}${toolMode === 'draw' ? ' drawing' : ''}`}
        onClick={handleLayerClick}
        onPointerDown={handleLayerPointerDown}
      >
        <svg className={`mapCollabSvg${toolMode === 'erase' ? ' erasing' : ''}`} viewBox="0 0 100 100" preserveAspectRatio="none">
          {drawings.map(d => {
            const erasable = toolMode === 'erase' && (d.authorUid === myUid || isDm);
            return (
              <g key={d.id}>
                <polyline
                  points={d.points}
                  fill="none"
                  stroke={d.color || myColor}
                  strokeWidth={d.width || STROKE_WIDTH}
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={erasable ? 'strokeErasable' : ''}
                />
                {erasable && (
                  <polyline
                    points={d.points}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={16}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                    onClick={() => deleteDrawing(d)}
                  />
                )}
              </g>
            );
          })}
          {drawPreview && drawPreview.length >= 2 && (
            <polyline
              points={drawPreview.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={myColor}
              strokeWidth={STROKE_WIDTH}
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
        {pins.map(pin => {
          const live = dragPos && dragPos.id === pin.id ? dragPos : pin;
          const mine = pin.authorUid === myUid;
          return (
            <button
              key={pin.id}
              className={`playerPin${mine ? ' playerPinMine' : ''}${dragPos?.id === pin.id ? ' playerPinDragging' : ''}`}
              style={{ left: `${live.x}%`, top: `${live.y}%`, background: pin.color || myColor }}
              onPointerDown={(e) => onPinPointerDown(e, pin)}
              onClick={(e) => e.stopPropagation()}
              title={pin.label}
            >
              <FontAwesomeIcon icon={getIconDef(pin.icon)} />
            </button>
          );
        })}
      </div>

      {selectedPin && (
        <div
          className="mapPinTooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y, transform: tooltipPos.above ? 'translate(-50%, calc(-100% - 0.75rem))' : 'translate(-50%, 0.75rem)' }}
        >
          <button className="mapTooltipClose" onClick={() => setSelected(null)}>×</button>
          <div className="mapTooltipIcon" style={{ color: selectedPin.color }}>
            <FontAwesomeIcon icon={getIconDef(selectedPin.icon)} />
          </div>
          <strong className="mapTooltipLabel">{selectedPin.label}</strong>
          {selectedPin.description && (
            <p className="mapTooltipDesc">{selectedPin.description}</p>
          )}
          <span className="mapTooltipAuthor">
            {i18next.t('map.playerPinBy', { name: selectedPin.authorName || '?' })}
          </span>
          {(selectedPin.authorUid === myUid || isDm) && (
            <div className="mapTooltipActions">
              {selectedPin.authorUid === myUid && (
                <button onClick={() => openEdit(selectedPin)} title={i18next.t('map.edit')}>
                  <FontAwesomeIcon icon={faPen} />
                </button>
              )}
              <button onClick={() => deletePin(selectedPin.id)} title={i18next.t('map.delete')}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          )}
        </div>
      )}

      {form && (
        <div className="mapPinFormOverlay" onClick={() => setForm(null)}>
          <div className="mapPinForm" onClick={e => e.stopPropagation()}>
            <h3>{form.id ? i18next.t('map.editPin') : i18next.t('map.addPlayerPin')}</h3>
            <div className="mapPinIconPicker">
              {PIN_ICONS.map(({ key, icon, label }) => (
                <button
                  key={key}
                  className={`mapPinIconOption${form.icon === key ? ' selected' : ''}`}
                  onClick={() => setForm({ ...form, icon: key })}
                  title={label}
                >
                  <FontAwesomeIcon icon={icon} />
                </button>
              ))}
            </div>
            <input
              className="mapPinInput"
              type="text"
              placeholder={i18next.t('map.labelPlaceholder')}
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              autoFocus
            />
            <textarea
              className="mapPinTextarea"
              placeholder={i18next.t('map.descriptionPlaceholder')}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
            <div className="mapPinFormActions">
              {form.id && (
                <button className="mapPinDeleteBtn" onClick={() => deletePin(form.id)}>
                  {i18next.t('map.delete')}
                </button>
              )}
              <button className="mapPinCancelBtn" onClick={() => setForm(null)}>{i18next.t('cancel')}</button>
              <button className="mapPinSaveBtn" onClick={savePin}>{i18next.t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div className="mapPinFormOverlay" onClick={() => setConfirm(null)}>
          <div className="mapPinForm mapConfirm" onClick={e => e.stopPropagation()}>
            <h3>{confirm === 'all' ? i18next.t('map.clearAll') : i18next.t('map.clearMine')}</h3>
            <p className="mapConfirmText">
              {confirm === 'all' ? i18next.t('map.clearAllConfirm') : i18next.t('map.clearMineConfirm')}
            </p>
            <div className="mapPinFormActions">
              <button className="mapPinCancelBtn" onClick={() => setConfirm(null)}>{i18next.t('cancel')}</button>
              <button className="mapPinDeleteBtn mapConfirmBtn" onClick={doConfirm}>
                {confirm === 'all' ? i18next.t('map.clearAll') : i18next.t('map.clearMine')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

MapCollab.displayName = 'MapCollab';

export default MapCollab;
